// The invite typeahead mixes two kinds of selections: existing users picked from
// the option list (value = username) and free-typed email addresses
// (CreatableSelect "new" options). The invite endpoint wants them split into
// user ids and emails.

export const inviteEmailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/

interface InviteSelection {
    value?: string
    __isNew__?: boolean
}

interface InviteUser {
    id?: number | string
    name?: string
    email?: string
}

export interface SentInvite {
    key: string
    label: string
}

interface InviteTeamPayload {
    pending_users?: InviteUser[] | null
    invitations?: Array<{ email?: string } | null> | null
}

/**
 * What to show as "invited just now" after a send, read back off the team the
 * invite endpoint returned. An email that belongs to an existing account comes
 * back as a pending user, so it is labelled with their handle rather than the
 * address that was typed.
 */
export function resolveSentInvites(
    recipients: { emails: string[]; ids: Array<number | string> },
    team: InviteTeamPayload | null | undefined,
    users: InviteUser[] | null | undefined,
): SentInvite[] {
    const pendingUsers = (team?.pending_users || []).filter(Boolean) as InviteUser[]
    const sent: SentInvite[] = []
    const seen = new Set<string>()

    const push = (key: string, label: string) => {
        if (seen.has(key)) return
        seen.add(key)
        sent.push({ key, label })
    }

    const findUser = (predicate: (user: InviteUser) => boolean) =>
        pendingUsers.find(predicate) || (users || []).filter(Boolean).find(predicate)

    recipients.ids.forEach(id => {
        const user = findUser(candidate => String(candidate?.id) === String(id))
        if (user?.name) push(`user-${id}`, `@${user.name}`)
    })

    recipients.emails.forEach(email => {
        const normalized = email.toLowerCase()
        // An address that resolved to an account reads better as their handle.
        const user = pendingUsers.find(
            candidate => String(candidate?.email || '').toLowerCase() === normalized,
        )
        if (user?.name) push(`user-${user.id}`, `@${user.name}`)
        else push(`email-${normalized}`, email)
    })

    return sent
}

export function splitInviteSelections(
    selections: InviteSelection[] | null | undefined,
    users: InviteUser[] | null | undefined,
): { emails: string[]; ids: Array<number | string> } {
    const emails: string[] = []
    const ids: Array<number | string> = []
    const seen = new Set<string>()

    ;(selections || []).forEach(selection => {
        const value = String(selection?.value || '').trim()
        if (!value) return

        if (selection?.__isNew__) {
            const key = `email:${value.toLowerCase()}`
            if (!inviteEmailRegex.test(value) || seen.has(key)) return
            seen.add(key)
            emails.push(value.toLowerCase())
        } else {
            const user = (users || []).find(candidate => candidate?.name === value)
            if (!user || user.id == null) return
            const key = `id:${user.id}`
            if (seen.has(key)) return
            seen.add(key)
            ids.push(user.id)
        }
    })

    return { emails, ids }
}
